/**
 * My To-dos — V5-04, `/todos`.
 *
 * Private tasks belonging to no project. Deliberately small: a line of text,
 * optionally a date and a priority, in an order you choose by hand.
 *
 * Nobody else can read these — not an admin, not the workspace owner. The
 * empty state says so, because a private space you cannot tell is private is
 * not much use as one.
 */

import { useMemo, useRef, useState } from 'react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, ListChecks, Lock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { DueChip } from '@/components/shared/TaskRow';
import { useDragSensors } from '@/lib/dndSensors';
import { useProjects } from '@/features/projects';
import {
  useConvertTodo,
  useCreateTodo,
  useDeleteTodo,
  useRestoreTodo,
  useTodos,
  useUpdateTodo,
  type TodoView,
} from './api';

const PRIORITY_DOT: Record<string, string> = {
  high: 'var(--danger)',
  normal: 'var(--accent)',
  low: 'var(--text-muted)',
};

function TodoRow({
  todo,
  onToggle,
  onRename,
  onDelete,
  onConvert,
  onDueDate,
  onPriority,
  projects,
}: {
  todo: TodoView;
  onToggle: (done: boolean) => void;
  onRename: (title: string) => void;
  onDelete: () => void;
  onConvert: (projectId: string) => void;
  onDueDate: (value: string | null) => void;
  onPriority: (value: string | null) => void;
  projects: { _id: string; name: string }[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: todo._id,
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(todo.title);

  const done = todo.status === 'done';

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`vt-surface flex items-center gap-2 px-2 py-1.5 ${isDragging ? 'opacity-40' : ''}`}
      data-testid="todo-row"
      data-title={todo.title}
      {...attributes}
      {...listeners}
    >
      <input
        type="checkbox"
        aria-label={`Complete ${todo.title}`}
        data-testid="todo-checkbox"
        checked={done}
        onChange={(e) => onToggle(e.target.checked)}
        // The row is draggable; without this a click on the box starts a drag.
        onPointerDown={(e) => e.stopPropagation()}
      />

      {todo.priority && (
        <span
          className="size-2 shrink-0 rounded-full"
          style={{ background: PRIORITY_DOT[todo.priority] }}
          title={`${todo.priority} priority`}
          aria-hidden
        />
      )}

      {editing ? (
        <Input
          className="h-6 flex-1"
          autoFocus
          value={draft}
          aria-label="Edit to-do"
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            setEditing(false);
            if (draft.trim() && draft !== todo.title) onRename(draft.trim());
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(todo.title);
              setEditing(false);
            }
          }}
        />
      ) : (
        <button
          type="button"
          data-testid="todo-title"
          className={`min-w-0 flex-1 truncate text-left text-[13px] ${done ? 'line-through vt-muted' : ''}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => !done && setEditing(true)}
        >
          {todo.title}
        </button>
      )}

      {todo.dueDate && <DueChip dueDate={todo.dueDate} done={done} />}

      {!done && (
        <input
          type="date"
          aria-label={`Due date for ${todo.title}`}
          data-testid="todo-due"
          className="vt-surface h-6 rounded-[6px] px-1 text-[11px]"
          value={todo.dueDate ? todo.dueDate.slice(0, 10) : ''}
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => onDueDate(e.target.value || null)}
        />
      )}

      {!done && (
        <select
          aria-label={`Priority for ${todo.title}`}
          data-testid="todo-priority"
          className="vt-surface h-6 rounded-[6px] px-1 text-[11px]"
          value={todo.priority ?? ''}
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => onPriority(e.target.value || null)}
        >
          <option value="">—</option>
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
        </select>
      )}

      {todo.convertedToTaskId && (
        <span className="vt-mono text-[11px] vt-muted" data-testid="todo-converted">
          → task
        </span>
      )}

      {!done && projects.length > 0 && (
        <select
          aria-label={`Move ${todo.title} to a project`}
          data-testid="todo-convert"
          className="vt-surface h-6 rounded-[6px] px-1 text-[11px]"
          value=""
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => e.target.value && onConvert(e.target.value)}
        >
          <option value="">Move to project…</option>
          {projects.map((p) => (
            <option key={p._id} value={p._id}>
              {p.name}
            </option>
          ))}
        </select>
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Delete ${todo.title}`}
        data-testid="todo-delete"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onDelete}
      >
        <Trash2 />
      </Button>
    </div>
  );
}

export function TodosPage() {
  const { data: open = [], isLoading } = useTodos('open');
  const { data: done = [] } = useTodos('done');
  const { data: projects = [] } = useProjects();

  const create = useCreateTodo();
  const update = useUpdateTodo();
  const remove = useDeleteTodo();
  const restore = useRestoreTodo();
  const convert = useConvertTodo();

  const [draft, setDraft] = useState('');
  const [showDone, setShowDone] = useState(false);
  const quickAdd = useRef<HTMLInputElement>(null);

  // V10 §7 — the shared sensors, which never take a key from an editable
  // element. The inline title editor lives inside a draggable row.
  const sensors = useDragSensors();

  const ids = useMemo(() => open.map((t) => t._id), [open]);

  function add(): void {
    const title = draft.trim();
    if (!title) return;
    setDraft('');
    // Focus is kept so a list of five can be typed without touching the mouse.
    quickAdd.current?.focus();
    create.mutate({ title });
  }

  function onDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = open.findIndex((t) => t._id === active.id);
    const to = open.findIndex((t) => t._id === over.id);
    if (from === -1 || to === -1) return;

    const movingDown = from < to;
    const afterId = movingDown ? open[to]._id : (open[to - 1]?._id ?? null);
    const beforeId = movingDown ? (open[to + 1]?._id ?? null) : open[to]._id;
    update.mutate({ id: String(active.id), afterId, beforeId });
  }

  function deleteWithUndo(todo: TodoView): void {
    remove.mutate(todo._id, {
      onSuccess: () => {
        toast('To-do deleted', {
          action: { label: 'Undo', onClick: () => restore.mutate(todo._id) },
        });
      },
    });
  }

  return (
    <>
      <PageHeader title="My To-dos" />

      <div className="flex flex-col gap-3 p-4" data-testid="todos-page">
        <div className="flex items-center gap-2">
          <Input
            ref={quickAdd}
            data-testid="todo-quick-add"
            aria-label="Add a to-do"
            placeholder="Add a to-do and press Enter…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add();
            }}
          />
          <Button data-testid="todo-add" onClick={add} disabled={!draft.trim()}>
            Add
          </Button>
        </div>

        <p className="flex items-center gap-1 text-[11px] vt-muted">
          <Lock className="size-3" />
          Only you can see these.
        </p>

        {isLoading ? (
          <Skeleton className="h-40" />
        ) : open.length === 0 ? (
          <EmptyState
            icon={<ListChecks className="size-6" />}
            title="No to-dos yet"
            description="Personal to-dos belong to no project and are private to you — only you can see these. Use them for the small things that are yours alone."
          />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-1" data-testid="todo-list">
                {open.map((todo) => (
                  <TodoRow
                    key={todo._id}
                    todo={todo}
                    projects={projects}
                    onToggle={(isDone) =>
                      update.mutate({ id: todo._id, status: isDone ? 'done' : 'open' })
                    }
                    onRename={(title) => update.mutate({ id: todo._id, title })}
                    onDueDate={(dueDate) => update.mutate({ id: todo._id, dueDate })}
                    onPriority={(priority) => update.mutate({ id: todo._id, priority })}
                    onDelete={() => deleteWithUndo(todo)}
                    onConvert={(projectId) =>
                      convert.mutate(
                        { id: todo._id, projectId },
                        {
                          onSuccess: (result) => {
                            const key = (result as { taskKey?: string })?.taskKey;
                            toast.success(key ? `Moved to ${key}` : 'Moved to project');
                          },
                        },
                      )
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {done.length > 0 && (
          <section data-testid="todo-done-group">
            <button
              type="button"
              className="flex items-center gap-1 text-[12px] vt-muted"
              data-testid="todo-done-toggle"
              onClick={() => setShowDone((v) => !v)}
            >
              {showDone ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
              Done ({done.length})
            </button>

            {showDone && (
              <div className="mt-1 flex flex-col gap-1">
                {done.map((todo) => (
                  <div
                    key={todo._id}
                    className="vt-surface flex items-center gap-2 px-2 py-1.5"
                    data-testid="todo-done-row"
                    data-title={todo.title}
                  >
                    <input
                      type="checkbox"
                      checked
                      aria-label={`Reopen ${todo.title}`}
                      onChange={() => update.mutate({ id: todo._id, status: 'open' })}
                    />
                    <span className="min-w-0 flex-1 truncate text-[13px] line-through vt-muted">
                      {todo.title}
                    </span>
                    {todo.convertedToTaskId && (
                      <span className="vt-mono text-[11px] vt-muted">→ task</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
}
